/** Administration layout: secondary navigation for tenant configuration screens. */
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { cn } from "@/shared/lib/format";

/** An admin sub-navigation entry. */
export interface AdminNavItem {
  to: string;
  labelKey: string;
  /** Any of these permissions shows the entry. */
  permission: string[];
}

/**
 * Admin entries in display order.
 *
 * Entries are gated on *management* permissions: plain ``*.view`` codes are
 * held by most operational roles (a technician needs ``identity.user.view`` to
 * pick an assignee) and must not surface the configuration area to them.
 */
export const ADMIN_NAV: AdminNavItem[] = [
  {
    to: "/admin/members",
    labelKey: "members.title",
    permission: ["identity.user.manage", "identity.user.invite", "identity.user.cost_rate"],
  },
  { to: "/admin/roles", labelKey: "roles.title", permission: ["identity.role.manage"] },
  { to: "/admin/teams", labelKey: "teams.title", permission: ["identity.team.manage"] },
  { to: "/admin/sso", labelKey: "sso.title", permission: ["identity.sso.manage"] },
  { to: "/admin/sites", labelKey: "sites.title", permission: ["locations.site.manage"] },
  { to: "/admin/parties", labelKey: "parties.title", permission: ["parties.party.manage"] },
  {
    to: "/admin/cost-centers",
    labelKey: "parties.costCenters.title",
    permission: ["parties.costcenter.view"],
  },
  {
    to: "/admin/asset-types",
    labelKey: "assetTypes.title",
    permission: [
      "assets.assettype.manage",
      "meters.meter.manage",
      "inventory.item.manage",
      "inventory.reorder.manage",
    ],
  },
  {
    to: "/admin/attributes",
    labelKey: "attributes.title",
    permission: ["platform.tenant.settings"],
  },
  {
    to: "/admin/job-templates",
    labelKey: "jobTemplates.title",
    permission: ["maintenance.jobtemplate.manage"],
  },
  { to: "/admin/plans", labelKey: "plans.title", permission: ["maintenance.plan.manage"] },
  {
    to: "/admin/document-settings",
    labelKey: "documentLibrary.settings",
    permission: ["documents.category.manage", "documents.document.governance"],
  },
  {
    to: "/admin/fault-automation",
    labelKey: "requests.automationTitle",
    permission: ["requests.automation.manage"],
  },
  {
    to: "/admin/notification-rules",
    labelKey: "notificationRules.title",
    permission: ["notifications.rule.manage"],
  },
  {
    to: "/admin/webhooks",
    labelKey: "webhooks.title",
    permission: ["integrations.webhook.manage", "integrations.events.read"],
  },
  { to: "/admin/audit", labelKey: "audit.title", permission: ["identity.audit.view"] },
];

/** Admin shell with a left sub-nav. */
export function AdminLayout(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const items = ADMIN_NAV.filter((i) => i.permission.some(can));
  return (
    <div className="flex min-w-0 flex-col gap-3 2xl:flex-row">
      <nav
        aria-label={t("app.administration")}
        className="flex max-w-full shrink-0 gap-1 overflow-x-auto pb-1 2xl:w-44 2xl:flex-col"
      >
        {items.map((i) => (
          <Link
            key={i.to}
            to={i.to}
            className={cn(
              "flex min-h-8 shrink-0 items-center rounded-sm border border-transparent px-2.5 py-1 text-xs hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring [@media(pointer:coarse)]:min-h-11",
              pathname.startsWith(i.to) &&
                "border-brand/40 border-s-2 border-s-brand bg-secondary font-semibold",
            )}
            aria-current={pathname.startsWith(i.to) ? "page" : undefined}
          >
            {t(i.labelKey)}
          </Link>
        ))}
      </nav>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

/** First admin route the current member may open (for the sidebar link). */
export function firstAdminRoute(can: (p: string) => boolean): string | null {
  return ADMIN_NAV.find((i) => i.permission.some(can))?.to ?? null;
}
